import { Extension } from '@tiptap/core';
import { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    search: {
      setSearchTerm: (searchTerm: string) => ReturnType;
    };
  }
}

interface TextWithNodePositions {
  text: string;
  nodePositions: { pos: number; type: string; text: string }[];
}

interface MentionResult {
  from?: number;
  to?: number;
  pos: number;
}

interface HighlightResults {
  nonMentionResults: DecorationSet;
  mentionResults: MentionResult[];
}

interface SearchMatches {
  startIndex: number;
  endIndex: number;
  length: number;
}

export interface SearchHighlightOptions {
  searchHighlightClass: string;
}

export interface SearchHighlightStorage {
  searchTerm: string;
  lastSearchTerm: string;
  mentionResults: MentionResult[];
}

const updateTextNodeWithPositions = (doc: PMNode): TextWithNodePositions[] => {
  let nodePositionArrayIndex = 0;
  let textWithNodePositions: TextWithNodePositions[] = [];
  doc.descendants((node, pos) => {
    if (['text', 'mention'].includes(node.type.name)) {
      let text = node.type.name === 'text' ? node.text || '' : node.attrs.label;
      if (textWithNodePositions[nodePositionArrayIndex]) {
        // Get the text from all nodes at the same level in the doc tree and concatenate them
        // while keeping a trach of independent node position in the doc tree
        textWithNodePositions[nodePositionArrayIndex].text += text;
        textWithNodePositions[nodePositionArrayIndex].nodePositions.push({
          pos,
          type: node.type.name,
          text,
        });
      } else {
        textWithNodePositions[nodePositionArrayIndex] = {
          text: `${text}`,
          nodePositions: [{ pos, type: node.type.name, text }],
        };
      }
    } else {
      nodePositionArrayIndex += 1;
    }
  });
  textWithNodePositions = textWithNodePositions.filter(Boolean);
  return textWithNodePositions;
};

const getSearchMatchesForNode = (text: string, searchTerm: RegExp): SearchMatches[] => {
  const searchMatches: SearchMatches[] = [];
  const matches = Array.from(text.matchAll(searchTerm)).filter(([matchText]) => matchText.trim());
  for (const m of matches) {
    if (m[0] === '') break;
    if (m.index !== undefined) {
      searchMatches.push({
        startIndex: m.index,
        length: m[0].length,
        endIndex: m.index + m[0].length,
      });
    }
  }
  return searchMatches;
};

const processNodesToHighlight = (
  nodesToHighlight: { pos: number; from: number; to: number; type: string }[],
  searchHighlightClass: string,
): {
  nonMentionResults: Decoration[];
  mentionResults: MentionResult[];
} => {
  const nonMentionResults: Decoration[] = [];
  const mentionResults: MentionResult[] = [];
  nodesToHighlight.forEach((nodeToHighlight) => {
    const { from, to, pos, type } = nodeToHighlight;
    if (type === 'text') {
      // Add a decoration for non-mention nodes so that tip-tap/prosemirror can highlight them
      const decoration: Decoration = Decoration.inline(pos + from, pos + to, {
        class: searchHighlightClass,
        nodeName: 'mark',
      });
      nonMentionResults.push(decoration);
    } else if (type === 'mention') {
      // Add attribute to mention nodes so that we can highlight them in the MentionNode component
      mentionResults.push({
        from: from,
        to: to,
        pos: pos,
      });
    }
  });
  return { nonMentionResults, mentionResults };
};

const getHighlightResults = (
  doc: PMNode | null = null,
  searchTerm: RegExp,
  searchHighlightClass: string,
  lastMentionResultsToReset: MentionResult[],
): HighlightResults => {
  if (!searchTerm || !doc) {
    return {
      nonMentionResults: DecorationSet.empty,
      mentionResults: [],
    };
  }

  const textWithNodePositions = updateTextNodeWithPositions(doc);
  const nodesToHighlight: { pos: number; from: number; to: number; type: string }[] = [];
  textWithNodePositions.forEach((textNodeWithPosition) => {
    const { text, nodePositions } = textNodeWithPosition;
    // Perform regex search and get matches to be highlighted for current paragraph/root node
    const searchMatches = getSearchMatchesForNode(text, searchTerm);

    if (searchMatches.length === 0) {
      return {
        nonMentionResults: DecorationSet.empty,
        mentionResults: [...lastMentionResultsToReset],
      };
    }

    // Adding allowedTotalIterations to avoid infinite loops since the while loop does not have a defined boundary
    const allowedTotalIterations = text.length * searchMatches.length;
    let currentIndex = 0,
      matchPos = 0,
      nodePos = 0,
      processedForMatch = 0,
      totalIterations = 0;
    // Using two pointers to advance and match nodes in the doc tree and matches from regex search
    while (currentIndex < text.length && nodePos < nodePositions.length) {
      if (matchPos >= searchMatches.length || totalIterations > allowedTotalIterations) break;
      let currentMatch = searchMatches[matchPos] || {};
      const { pos, type, text: nodeText } = nodePositions[nodePos];
      const startIndexForMatch = currentMatch.startIndex + processedForMatch;
      // If current match is fully highlighted already, advance to the next match while keeping the same node
      const matchFullyProcessed =
        (currentMatch.startIndex !== currentMatch.endIndex &&
          currentMatch.endIndex <= startIndexForMatch) ||
        processedForMatch >= currentMatch.length;
      if (matchFullyProcessed) {
        matchPos++;
        processedForMatch = 0;
        continue;
      }
      // Node needs to be highlighted if the match start is within the node text range
      if (startIndexForMatch < currentIndex + nodeText.length) {
        const from = startIndexForMatch - currentIndex;
        const to =
          currentMatch.endIndex - currentIndex > nodeText.length
            ? nodeText.length
            : currentMatch.endIndex - currentIndex;
        nodesToHighlight.push({ from, to, pos, type });
        processedForMatch += to - from;
        // Keep a track of how much text of the current match is already highlighted
        if (from === to) processedForMatch++;
      } else {
        nodePos++;
        currentIndex += nodeText.length;
      }
      totalIterations++;
    }
  });

  const { nonMentionResults, mentionResults } = processNodesToHighlight(
    nodesToHighlight,
    searchHighlightClass,
  );

  return {
    nonMentionResults: DecorationSet.create(doc, nonMentionResults),
    mentionResults: [...lastMentionResultsToReset, ...mentionResults],
  };
};

export const SearchHighlight = Extension.create<SearchHighlightOptions, SearchHighlightStorage>({
  name: 'searchHighlight',

  addOptions() {
    return {
      searchHighlightClass: 'highlighted-text',
    };
  },

  addStorage() {
    return {
      searchTerm: '',
      mentionResults: [],
      lastSearchTerm: '',
    };
  },

  addCommands() {
    return {
      setSearchTerm:
        (searchTerm: string) =>
        ({ editor }) => {
          editor.storage.searchHighlight.searchTerm = searchTerm;
          return false;
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const { searchHighlightClass } = this.options;
    const setLastSearchTerm = (lastSearchTerm: string) =>
      (editor.storage.searchHighlight.lastSearchTerm = lastSearchTerm);

    return [
      new Plugin({
        key: new PluginKey('searchHighlightPlugin'),
        state: {
          init: () => DecorationSet.empty,
          apply({ doc, docChanged }, oldState) {
            const {
              searchTerm,
              lastSearchTerm,
              mentionResults: lastMentionResults,
            } = editor.storage.searchHighlight;

            if (!docChanged && lastSearchTerm === searchTerm) return oldState;

            setLastSearchTerm(searchTerm);

            const lastMentionResultsToReset = (lastMentionResults || []).map(
              (mentionResult: MentionResult) => ({
                ...mentionResult,
                from: undefined,
                to: undefined,
              }),
            );

            if (!searchTerm) {
              editor.storage.searchHighlight.mentionResults = [...lastMentionResultsToReset];
              editor.storage.searchHighlight.searchTerm = '';
              return DecorationSet.empty;
            }

            const escapedSearchTerm = String(searchTerm).replace(/[\\^$*+?.()|[\]{}]/g, '\\$&');
            const { nonMentionResults, mentionResults } = getHighlightResults(
              doc,
              RegExp(escapedSearchTerm, 'gui'),
              searchHighlightClass,
              lastMentionResultsToReset,
            );

            editor.storage.searchHighlight.mentionResults = mentionResults;
            return nonMentionResults;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

export default SearchHighlight;
